/* eslint-disable no-restricted-syntax */
import api from './config';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';

const initialState = {
  notifications: [],
  loading: false,
  isSuccess: false,
  errors: []
};

export const fetchAllNotification = createAsyncThunk(
  'notification/fetchAllNotification',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const { data } = await api.get('/notification/receiver', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      return data;
    } catch (error) {
      if (error.response && error.response.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const markAsViewNotification = createAsyncThunk(
  'notification/markAsViewNotification',
  async (notifId, { rejectWithValue, dispatch }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const { data } = await api.put(`/notification/${notifId}`, null, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      // On ne refetch pas forcément ici, on met à jour le store localement.
      return data;
    } catch (error) {
      if (error.response && error.response.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

/** Marquer comme lues toutes les notifications « message » d'une réclamation */
export const markClaimMessageNotificationsViewed = createAsyncThunk(
  'notification/markClaimMessageNotificationsViewed',
  async (claimId, { rejectWithValue, dispatch }) => {
    try {
      await api.put(`/notification/claims/${claimId}/msg/view`);
      return String(claimId);
    } catch (error) {
      if (error.response && error.response.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

/** Supprimer UNE notification */
export const deleteNotification = createAsyncThunk(
  'notification/deleteNotification',
  async (notifId, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/notification/${notifId}`);
      return notifId; // retourne l'id pour filtrer dans le reducer
    } catch (error) {
      if (error.response && error.response.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

/** Supprimer TOUTES les notifications lues */
export const deleteAllReadNotifications = createAsyncThunk(
  'notification/deleteAllReadNotifications',
  async (_, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const list = state.notifications.notifications || [];
      const read = list.filter((n) => n.view);

      await Promise.all(
        read.map((n) =>
          api.delete(`/notification/${n._id}`).catch(() => null)
        )
      );

      // retourne la liste des ids supprimés pour nettoyer le store
      return read.map((n) => n._id);
    } catch (error) {
      if (error.response && error.response.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllNotification.pending, (state) => {
        state.loading = true;
        state.isSuccess = false;
        state.errors = [];
      })
      .addCase(fetchAllNotification.fulfilled, (state, { payload }) => {
        state.notifications = payload;
        state.isSuccess = true;
        state.loading = false;
      })
      .addCase(fetchAllNotification.rejected, (state, { payload }) => {
        state.errors =
          payload?.statusCode === 400
            ? payload?.message
            : ['Failed to fetch notifications.'];
        state.isSuccess = false;
        state.loading = false;
      })

      .addCase(markAsViewNotification.pending, (state) => {
        state.loading = true;
        state.isSuccess = false;
        state.errors = [];
      })
      .addCase(markAsViewNotification.fulfilled, (state, { payload }) => {
        state.notifications = state.notifications.map((n) =>
          n._id === payload._id ? { ...n, view: true } : n
        );
        state.isSuccess = true;
        state.loading = false;
      })
      .addCase(markAsViewNotification.rejected, (state, { payload }) => {
        state.errors =
          payload?.statusCode === 400
            ? payload?.message
            : ['Failed to mark notification as viewed.'];
        state.isSuccess = false;
        state.loading = false;
      })

      .addCase(markClaimMessageNotificationsViewed.fulfilled, (state, { payload: claimId }) => {
        state.notifications = state.notifications.map((n) =>
          String(n.claimId) === claimId && ['message', 'msg'].includes(n.kind)
            ? { ...n, view: true }
            : n
        );
      })

      .addCase(deleteNotification.fulfilled,(state, { payload: notifId }) => {
        state.notifications = state.notifications.filter((n) => n._id !== notifId);
      })

      .addCase(deleteAllReadNotifications.fulfilled, (state, { payload: ids }) => {
        const idSet = new Set(ids);
        state.notifications = state.notifications.filter((n) => !idSet.has(n._id));
      });
  }
});

export default notificationSlice.reducer;
